import type { FavoriteViewSearch } from '@/contexts/favorites/favoriteView'
import { ShellBffHttpError } from '@offer/bff-client-core'
import { buildFavoriteViewUrl } from '@/contexts/favorites/buildFavoriteViewUrl'
import { mockFavoriteViewFolders } from '~/__mocks__/catalog/favorite-folders'
import { mockFavoriteViews } from '~/__mocks__/catalog/favorite-views'
// import * as apiBff from './bff'

export interface FamilyDepartmentGroup {
  departmentId: number
  familyIds: number[]
}

export interface HierarchyIssue {
  code: 'FAMILIES_SPAN_MULTIPLE_DEPARTMENTS'
  departments: FamilyDepartmentGroup[]
}

export interface FavoriteView {
  id: string
  title: string
  folderId: string | null
  search: FavoriteViewSearch
  supermodelCount?: number
  previewImageUrl?: string | null
  hierarchyIssue?: HierarchyIssue
  createdAt: string
  updatedAt: string
}

export interface FavoriteViewFolder {
  id: string
  name: string
  emoji?: string | null
  viewCount: number
  createdAt?: string | null
  updatedAt?: string | null
}

export interface FavoriteViewList {
  items: FavoriteView[]
  total: number
}

export interface FavoriteViewFolderList {
  items: FavoriteViewFolder[]
}

export interface FavoriteViewShareLink {
  url: string
}

export interface CreateFavoriteViewFolderInput {
  name: string
  emoji?: string | null
}

export interface UpdateFavoriteViewFolderInput {
  name?: string
  emoji?: string | null
}

export interface CreateFavoriteViewInput {
  title: string
  folderId?: string
  search: FavoriteViewSearch
}

export interface UpdateFavoriteViewInput {
  title?: string
  folderId?: string | null
}

let nextMockFolderId = 1
let nextMockViewId = 1
let favoriteViews = [...mockFavoriteViews.items]
let favoriteViewFolders = [...mockFavoriteViewFolders.items]

function createTimestamp() {
  return new Date().toISOString()
}

function notFound(resource: string, method: string, path: string) {
  return new ShellBffHttpError(`${resource} not found`, 404, method, path)
}

function folderConflict() {
  return new ShellBffHttpError('Conflict', 409, 'POST', '/favorites/folders')
}

export function getFavoritesViews(folderId?: string, _signal?: AbortSignal): Promise<FavoriteViewList> {
  // return apiBff.callBFF('GET', '/favorites/views', undefined, { signal })
  if (folderId && !favoriteViewFolders.some(folder => folder.id === folderId))
    return Promise.reject(notFound('Favorite view folder', 'GET', `/favorites/folders/${folderId}`))

  const items = favoriteViews.filter(view => folderId === undefined || view.folderId === folderId)

  return Promise.resolve({ items, total: items.length })
}

export function getFavoriteView(viewId: string, _signal?: AbortSignal): Promise<FavoriteView> {
  const favoriteView = favoriteViews.find(view => view.id === viewId)

  if (!favoriteView)
    return Promise.reject(notFound('Favorite view', 'GET', `/favorites/views/${viewId}`))

  return Promise.resolve(favoriteView)
}

export function getFavoriteViewShareLink(viewId: string, _signal?: AbortSignal): Promise<FavoriteViewShareLink> {
  const path = `/favorites/views/${encodeURIComponent(viewId)}/share-link`
  const favoriteView = favoriteViews.find(view => view.id === viewId)

  if (!favoriteView)
    return Promise.reject(notFound('Favorite view', 'GET', path))

  const relativeUrl = buildFavoriteViewUrl(favoriteView.search)
  if (!relativeUrl)
    return Promise.reject(new ShellBffHttpError('Share link unavailable', 422, 'GET', path))

  // return apiBff.callBFF('GET', path, undefined, { signal: _signal })
  return Promise.resolve({ url: new URL(relativeUrl, globalThis.location.origin).href })
}

export function getFavoriteViewFolders(_signal?: AbortSignal): Promise<FavoriteViewFolderList> {
  // return apiBff.callBFF('GET', '/favorites/folders', undefined, { signal: _signal })
  return Promise.resolve({ items: favoriteViewFolders })
}

export function createFavoriteViewFolder(input: CreateFavoriteViewFolderInput): Promise<FavoriteViewFolder> {
  // return apiBff.callBFF('POST', '/favorites/folders', input)
  if (favoriteViewFolders.some(folder => folder.name.trim().toLocaleLowerCase() === input.name.trim().toLocaleLowerCase()))
    return Promise.reject(folderConflict())

  const timestamp = createTimestamp()

  const folder: FavoriteViewFolder = {
    id: `mock-folder-${nextMockFolderId++}`,
    name: input.name,
    emoji: input.emoji ?? null,
    viewCount: 0,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
  favoriteViewFolders = [...favoriteViewFolders, folder]

  return Promise.resolve(folder)
}

export function updateFavoriteViewFolder(
  folderId: string,
  input: UpdateFavoriteViewFolderInput,
): Promise<FavoriteViewFolder> {
  const folder = favoriteViewFolders.find(item => item.id === folderId)

  if (!folder)
    return Promise.reject(notFound('Favorite view folder', 'PATCH', `/favorites/folders/${folderId}`))

  if (input.name && favoriteViewFolders.some(item => item.id !== folderId
    && item.name.trim().toLocaleLowerCase() === input.name!.trim().toLocaleLowerCase())) {
    return Promise.reject(folderConflict())
  }

  const updatedFolder = { ...folder, ...input, updatedAt: createTimestamp() }
  favoriteViewFolders = favoriteViewFolders.map(item => item.id === folderId ? updatedFolder : item)

  return Promise.resolve(updatedFolder)
}

export function deleteFavoriteViewFolder(folderId: string): Promise<void> {
  // return apiBff.callBFF('DELETE', `/favorites/folders/${encodeURIComponent(folderId)}`)
  const folderExists = favoriteViewFolders.some(folder => folder.id === folderId)

  if (!folderExists)
    return Promise.reject(notFound('Favorite view folder', 'DELETE', `/favorites/folders/${folderId}`))

  favoriteViewFolders = favoriteViewFolders.filter(folder => folder.id !== folderId)
  favoriteViews = favoriteViews.filter(view => view.folderId !== folderId)

  return Promise.resolve()
}

export function createFavoriteView(input: CreateFavoriteViewInput): Promise<FavoriteView> {
  // return apiBff.callBFF('POST', 'v1/favorites/views', input)
  if (input.folderId && !favoriteViewFolders.some(folder => folder.id === input.folderId))
    return Promise.reject(notFound('Favorite view folder', 'POST', `/favorites/folders/${input.folderId}`))

  const timestamp = createTimestamp()

  const favoriteView: FavoriteView = {
    id: `mock-view-${nextMockViewId++}`,
    title: input.title,
    folderId: input.folderId ?? null,
    search: input.search,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
  favoriteViews = [...favoriteViews, favoriteView]
  if (favoriteView.folderId) {
    favoriteViewFolders = favoriteViewFolders.map(folder => folder.id === favoriteView.folderId
      ? { ...folder, viewCount: folder.viewCount + 1 }
      : folder)
  }

  return Promise.resolve(favoriteView)
}

export function updateFavoriteView(viewId: string, input: UpdateFavoriteViewInput): Promise<FavoriteView> {
  const favoriteView = favoriteViews.find(view => view.id === viewId)

  if (!favoriteView)
    return Promise.reject(notFound('Favorite view', 'PATCH', `/favorites/views/${viewId}`))

  if (input.folderId !== undefined && input.folderId !== null && !favoriteViewFolders.some(folder => folder.id === input.folderId))
    return Promise.reject(notFound('Favorite view folder', 'PATCH', `/favorites/folders/${input.folderId}`))

  const previousFolderId = favoriteView.folderId
  const updatedView = { ...favoriteView, ...input, updatedAt: createTimestamp() }
  favoriteViews = favoriteViews.map(view => view.id === viewId ? updatedView : view)
  const nextFolderId = updatedView.folderId

  if (previousFolderId !== nextFolderId) {
    favoriteViewFolders = favoriteViewFolders.map((folder) => {
      if (folder.id === previousFolderId)
        return { ...folder, viewCount: Math.max(0, folder.viewCount - 1) }
      if (folder.id === nextFolderId)
        return { ...folder, viewCount: folder.viewCount + 1 }
      return folder
    })
  }

  return Promise.resolve(updatedView)
}

export function deleteFavoriteView(viewId: string): Promise<void> {
  // return apiBff.callBFF('DELETE', `v1/favorites/views/${encodeURIComponent(viewId)}`)
  const favoriteView = favoriteViews.find(view => view.id === viewId)

  if (!favoriteView)
    return Promise.reject(notFound('Favorite view', 'DELETE', `/favorites/views/${viewId}`))

  favoriteViews = favoriteViews.filter(view => view.id !== viewId)
  if (favoriteView.folderId) {
    favoriteViewFolders = favoriteViewFolders.map(folder => folder.id === favoriteView.folderId
      ? { ...folder, viewCount: Math.max(0, folder.viewCount - 1) }
      : folder)
  }

  return Promise.resolve()
}
